import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({
  children,
  allowedRoles = [],
  redirectTo = '/login',
  unauthorizedRedirect = '/dashboard'
}) => {
  const { isAuthenticated, profile, loading, profileLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth state
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-[#00AAFF]/30 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-[#00AAFF] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="mt-4 text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified
  if (allowedRoles.length > 0) {
    const userRole = profile?.role ?? null;

    if (!userRole || !allowedRoles.includes(userRole)) {
      // User doesn't have the required role
      // Redirect to appropriate page based on their actual role
      const roleRedirects = {
        admin: '/admin/dashboard',
        agent: '/agent/dashboard',
        partner: '/dashboard',
        student: '/student/dashboard',
      };

      const fallbackRedirect = roleRedirects[userRole] || unauthorizedRedirect;
      return <Navigate to={fallbackRedirect} replace />;
    }
  }

  return children;
};

// Higher-order component for role-specific routes
export const withRoleAccess = (allowedRoles) => {
  return function RoleProtectedRoute({ children }) {
    return (
      <ProtectedRoute allowedRoles={allowedRoles}>
        {children}
      </ProtectedRoute>
    );
  };
};

// Pre-configured role-specific route components
export const AdminRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={['admin']}>
    {children}
  </ProtectedRoute>
);

export const AgentRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={['agent', 'admin']}>
    {children}
  </ProtectedRoute>
);

export const PartnerRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={['partner', 'agent', 'admin']}>
    {children}
  </ProtectedRoute>
);

export const StudentRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={['student']}>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
